import { useEffect } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";

import {
  AppText,
  Button,
  ButtonText,
  Card,
  ProtectedScreen,
  Row,
  Stack,
} from "~/components/ui";
import { useNotifications } from "~/hooks/useNotifications";

function normalizeParam(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] : value;
}

function canOpenInternalPath(value: string) {
  return value.startsWith("/") && !value.startsWith("//");
}

function formatNotificationDate(value: string) {
  return new Intl.DateTimeFormat("en-NZ", {
    dateStyle: "full",
    timeStyle: "short",
  }).format(new Date(value));
}

export default function NotificationDetailScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ id: string | string[] }>();
  const id = normalizeParam(params.id);
  const { notifications, loading, markRead } = useNotifications();
  const notification = notifications.find((item) => item.id === id);
  const linkedPath =
    notification && canOpenInternalPath(notification.url)
      ? notification.url
      : null;

  useEffect(() => {
    if (notification && !notification.readAt) void markRead(notification.id);
  }, [markRead, notification]);

  return (
    <ProtectedScreen title={notification?.title ?? "Notification"}>
      {loading ? <AppText>Loading notification...</AppText> : null}
      {!loading && !notification ? (
        <AppText tone="danger">Notification not found.</AppText>
      ) : null}
      {notification ? (
        <Card gap="sm">
          <Stack gap="sm">
            <AppText size="xs">
              {formatNotificationDate(notification.created_at)}
            </AppText>
            <AppText>{notification.body}</AppText>
            <Row>
              {linkedPath ? (
                <Button
                  compact
                  iconName="arrow-right"
                  iconPosition="right"
                  onPress={() => router.push(linkedPath as never)}
                >
                  <ButtonText>Open linked item</ButtonText>
                </Button>
              ) : null}
              <Button
                compact
                variant="outline"
                onPress={() => router.push("/notifications" as never)}
              >
                <ButtonText>Back to inbox</ButtonText>
              </Button>
            </Row>
          </Stack>
        </Card>
      ) : null}
    </ProtectedScreen>
  );
}
